"use client";
import React from 'react';
import { useState, useEffect } from "react";
import { tarotReading_openrouter, tarotReading_groq } from "@/lib/ai";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Send, Sparkles, Info } from "lucide-react";
import { useTranslations } from 'next-intl';
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { SmartText, SmartTitle } from "@/components/ui/SmartFont";
import ReactMarkdown from 'react-markdown';
import ShareButtons from "@/components/ui/ShareButtons";
import { saveTarotReading, TarotReading as TarotReadingType } from "@/lib/supabase";

// 定义塔罗师类型
type ReaderType = 'direct' | 'kind';

// 定义塔罗师配置
const readerConfigs = {
    direct: {
        bgGradient: 'from-red-600/50 to-orange-600/50',
        borderColor: 'border-red-400',
        accentColor: 'text-red-400',
        hoverBg: 'hover:from-red-700/50 hover:to-orange-700/50',
        cardBg: 'bg-red-900/30',
        image: 'https://images.tarotcardgenerator.org/unconventional-teller.png',
        inputBg: 'from-red-950/80 to-orange-950/80',
        buttonGradient: 'from-red-600 to-orange-600',
        buttonHover: 'hover:from-red-700 hover:to-orange-700'
    },
    kind: {
        bgGradient: 'from-indigo-600/50 to-purple-600/50',
        borderColor: 'border-purple-400',
        accentColor: 'text-purple-400',
        hoverBg: 'hover:from-indigo-700/50 hover:to-purple-700/50',
        cardBg: 'bg-purple-900/30',
        image: 'https://images.tarotcardgenerator.org/traditional-teller.png',
        inputBg: 'from-indigo-950/80 to-purple-950/80',
        buttonGradient: 'from-purple-600 to-indigo-600',
        buttonHover: 'hover:from-purple-700 hover:to-indigo-700'
    }
};

// 修改响应接口，从结构化转为文本
interface TarotResponse {
    text: string;
    selectedCard: string;
    isSuitableForTarot: boolean;
}

const cards = [
    // 圣杯牌组 (Cups)
    'aceofcups', 'twoofcups', 'threeofcups', 'fourofcups', 'fiveofcups',
    'sixofcups', 'sevenofcups', 'eightofcups', 'nineofcups', 'tenofcups',
    'pageofcups', 'knightofcups', 'queenofcups', 'kingofcups',

    // 权杖牌组 (Wands)
    'aceofwands', 'twoofwands', 'threeofwands', 'fourofwands', 'fiveofwands',
    'sixofwands', 'sevenofwands', 'eightofwands', 'nineofwands', 'tenofwands',
    'pageofwands', 'knightofwands', 'queenofwands', 'kingofwands',

    // 宝剑牌组 (Swords)
    'aceofswords', 'twoofswords', 'threeofswords', 'fourofswords', 'fiveofswords',
    'sixofswords', 'sevenofswords', 'eightofswords', 'nineofswords', 'tenofswords',
    'pageofswords', 'knightofswords', 'queenofswords', 'kingofswords',

    // 钱币牌组 (Pentacles)
    'aceofpentacles', 'twoofpentacles', 'threeofpentacles', 'fourofpentacles', 'fiveofpentacles',
    'sixofpentacles', 'sevenofpentacles', 'eightofpentacles', 'nineofpentacles', 'tenofpentacles',
    'pageofpentacles', 'knightofpentacles', 'queenofpentacles', 'kingofpentacles',

    // 大阿卡纳牌组 (Major Arcana)
    'thefool', 'themagician', 'thehighpriestess', 'theempress', 'theemperor',
    'thehierophant', 'thelovers', 'thechariot', 'strength', 'thehermit',
    'wheelfortune', 'justice', 'thehangedman', 'death', 'temperance',
    'thedevil', 'thetower', 'thestar', 'themoon', 'thesun',
    'judgement', 'theworld'
];

export default function TarotReading(): JSX.Element {
    const t = useTranslations('tarotreading');
    const tc = useTranslations('tarotcard');
    const language: string = t('language');
    const [question, setQuestion] = useState<string>("");
    const [response, setResponse] = useState<TarotResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showCard, setShowCard] = useState<boolean>(false);
    const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);
    const [showStars, setShowStars] = useState<boolean>(true);
    const [showTip, setShowTip] = useState<boolean>(false);
    // 塔罗师类型状态
    const [readerType, setReaderType] = useState<ReaderType>('direct');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isReversed, setIsReversed] = useState(false);
    const [useReversals, setUseReversals] = useState(false); // 是否启用逆位模式
    const [savedReading, setSavedReading] = useState<{id: string, share_id: string} | null>(null);
    // 新增保存状态追踪
    const [isSaved, setIsSaved] = useState<boolean>(false);
    
    // 获取当前塔罗师配置
    const config = readerConfigs[readerType];

    // 将响应文本拆分为第一阶段和第二阶段
    const [firstStageText, setFirstStageText] = useState<string>("");
    const [secondStageText, setSecondStageText] = useState<string>("");

    // 添加一个新的状态来跟踪剩余字符数
    const [characterCount, setCharacterCount] = useState<number>(0);
    const MAX_CHARS = 200; // 设置最大字符数

    useEffect(() => {
        // 组件挂载后显示星星动画
        setShowStars(true);

        // 如果有响应，滚动到视图
        if (response) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });

            // 将响应文本拆分为两个阶段
            const textParts = response.text.split("\n\n");
            if (textParts.length >= 2) {
                setFirstStageText(textParts[0]);
                setSecondStageText(textParts.slice(1).join("\n\n"));
            } else {
                setFirstStageText(response.text);
                setSecondStageText("");
            }
        }
    }, [response]);

    // 优化后的保存逻辑
    useEffect(() => {
        // 确保所有必要的数据都存在且尚未保存过当前会话
        const saveTarotReadingToSupabase = async () => {
            if (response && firstStageText && secondStageText && !isSaved) {
                try {
                    // 标记为已保存，防止重复保存
                    setIsSaved(true);
                    
                    // 准备要保存的数据
                    const tarotReading: TarotReadingType = {
                        question: question,
                        selected_card: response.selectedCard,
                        is_reversed: isReversed,
                        reader_type: readerType,
                        reading_text: response.text,
                        first_stage_text: firstStageText,
                        second_stage_text: secondStageText,
                        language: language
                    };
                    
                    // 保存到Supabase
                    const savedData = await saveTarotReading(tarotReading);
                    if (savedData) {
                        setSavedReading(savedData);
                        console.log("保存的塔罗牌解读ID:", savedData.id);
                        console.log("分享ID:", savedData.share_id);
                    }
                } catch (saveError) {
                    console.error("保存塔罗牌解读失败:", saveError);
                    // 保存失败时重置标记，允许下次重试
                    setIsSaved(false);
                }
            }
        };

        saveTarotReadingToSupabase();
    }, [response, firstStageText, secondStageText, question, isReversed, readerType, language, isSaved]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        if (!question.trim()) return;

        setIsLoading(true);
        setShowCard(false);
        setResponse(null);
        setIsImageLoaded(false);
        setErrorMessage(null);
        setFirstStageText("");
        setSecondStageText("");
        setSavedReading(null);
        // 重置保存状态，允许新的问答结果保存
        setIsSaved(false);

        const selectedCard = cards[Math.floor(Math.random() * cards.length)];
        // 如果启用逆位模式，则有50%概率抽到逆位牌
        const cardIsReversed = useReversals ? Math.random() < 0.5 : false;
        // const cardIsReversed = true;
        setIsReversed(cardIsReversed);
        
        const promptText = `${question}\n\nrespond with ${language}`;

        try {
            // 先尝试使用OpenRouter API
            console.log("尝试使用OpenRouter API...");
            try {
                const openRouterResult = await Promise.race([
                    tarotReading_openrouter(promptText, selectedCard, language, readerType, cardIsReversed),
                    new Promise<never>((_, reject) => setTimeout(() => reject(new Error("OpenRouter API超时")), 20000))
                ]) as {text: string, isSuitableForTarot: boolean};
                
                // 直接使用API返回的结果设置response
                setResponse({
                    text: openRouterResult.text,
                    selectedCard: selectedCard,
                    isSuitableForTarot: openRouterResult.isSuitableForTarot
                });
            } catch (openRouterError) {
                console.error("OpenRouter API错误:", openRouterError);
                
                // 尝试使用Groq API作为备选
                console.log("尝试使用Groq API...");
                try {
                    const groqResult = await Promise.race([
                        tarotReading_groq(promptText, selectedCard, language, readerType, cardIsReversed),
                        new Promise((_, reject) => setTimeout(() => reject(new Error("Groq API超时")), 20000))
                    ]) as {text: string, isSuitableForTarot: boolean};
                    
                    // 直接使用API返回的结果设置response
                    setResponse({
                        text: groqResult.text,
                        selectedCard: selectedCard,
                        isSuitableForTarot: groqResult.isSuitableForTarot
                    });
                } catch (groqError) {
                    console.error("Groq API错误:", groqError);
                    
                    // 所有可用API都失败了
                    setErrorMessage(language === '中文' 
                        ? "抱歉，塔罗系统暂时不可用。请稍后再试或联系工作人员：magictarot@tarotcardgenerator.org"
                        : "Sorry, the tarot system is temporarily unavailable. Please try again later or contact support: magictarot@tarotcardgenerator.org");
                    setResponse(null);
                }
            }
            
        } catch (error) {
            console.error("API调用错误:", error);
            setErrorMessage(language === '中文' 
                ? "抱歉，塔罗系统暂时不可用。请稍后再试或联系工作人员：magictarot@tarotcardgenerator.org"
                : "Sorry, the tarot system is temporarily unavailable. Please try again later or contact support: magictarot@tarotcardgenerator.org");
            setResponse(null);
        } finally {
            setIsLoading(false);
        }
    };

    const renderPreFlipMessage = (): JSX.Element | null => {
        if (!response || !firstStageText) return null;

        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className={`p-6 rounded-lg ${config.cardBg} backdrop-blur-sm border ${config.borderColor}/20 shadow-lg`}
            >
                <div className="whitespace-pre-wrap text-white/90">
                    <ReactMarkdown>
                        {firstStageText}
                    </ReactMarkdown>
                </div>
            </motion.div>
        );
    };

    const renderCardContent = (): JSX.Element | null => {
        if (!response || !secondStageText) return null;

        // 直接使用isSuitableForTarot属性来判断是否渲染塔罗牌
        if (!response.isSuitableForTarot) return null;

        return (
            <>
                {!showCard ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                    >
                        <Button
                            onClick={() => setShowCard(true)}
                            className={`w-full py-6 text-lg bg-gradient-to-r ${config.buttonGradient} ${config.buttonHover} shadow-md hover:shadow-lg transition-all duration-300 border ${config.borderColor}/20`}
                        >
                            <Sparkles className="w-5 h-5 mr-2" />
                            <SmartText>{t("reveal_card")}</SmartText>
                        </Button>
                    </motion.div>
                ) : (
                    <>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8, rotateY: 180 }}
                            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                            transition={{
                                type: "spring",
                                stiffness: 100,
                                duration: 0.8
                            }}
                            className="flex justify-center py-6"
                        >
                            <div className="w-full max-w-[300px] mx-auto">
                                <img
                                    src={`https://images.tarotcardgenerator.org/${response.selectedCard}.webp`}
                                    alt={response.selectedCard}
                                    className={`w-full rounded-lg shadow-xl relative z-10 transition-transform duration-700 ${isReversed ? 'rotate-180' : ''}`}
                                    onLoad={() => setIsImageLoaded(true)}
                                    style={{ maxHeight: '450px', objectFit: 'contain' }}
                                />
                            </div>
                        </motion.div>

                        <AnimatePresence>
                            {isImageLoaded && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5, duration: 0.6 }}
                                    className={`p-6 rounded-lg ${config.cardBg} backdrop-blur-sm mt-6 border ${config.borderColor}/20 shadow-lg`}
                                >
                                    <h3 className={`text-xl font-semibold mb-4 text-center ${config.accentColor}`}>
                                        <SmartTitle>
                                            {tc(response.selectedCard) || response.selectedCard}
                                            {isReversed && <span className="ml-2 text-sm opacity-80">{tc("reversed")}</span>}
                                        </SmartTitle>
                                    </h3>
                                    <div className="whitespace-pre-wrap text-white/90">
                                        <ReactMarkdown>
                                            {secondStageText}
                                        </ReactMarkdown>
                                    </div>

                                    {/* Add ShareButtons component */}
                                    <div className="mt-6 pt-6 border-t border-white/10">
                                        <ShareButtons
                                            url={
                                                savedReading?.share_id 
                                                ? `${window.location.origin}/share/${savedReading.share_id}` 
                                                : window.location.href
                                            }
                                            title={t('share_title')}
                                            gradientClass={config.buttonGradient}
                                            readingId={savedReading?.id}
                                            shareId={savedReading?.share_id}
                                            asStaticPage={true}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </>
                )}
            </>
        );
    };

    // 创建星星
    const renderStars = () => {
        if (!showStars) return null;

        const stars = Array.from({ length: 50 }).map((_, i) => {
            const size = Math.random() * 3 + 1;
            const left = `${Math.random() * 100}%`;
            const top = `${Math.random() * 100}%`;
            const animationDuration = `${Math.random() * 3 + 2}s`;
            const delay = `${Math.random() * 2}s`;

            return (
                <div
                    key={i}
                    className="absolute rounded-full bg-white"
                    style={{
                        width: `${size}px`,
                        height: `${size}px`,
                        left,
                        top,
                        opacity: Math.random() * 0.7 + 0.3,
                        animation: `twinkle ${animationDuration} infinite alternate`,
                        animationDelay: delay,
                    }}
                />
            );
        });

        return stars;
    };

    // 渲染塔罗师选择侧边栏
    const renderReaderSidebar = () => {
        const config = readerConfigs[readerType];
        
        return (
            <div className="w-full md:w-1/4 flex flex-col bg-gradient-to-br from-slate-900/90 to-slate-950/95 rounded-xl backdrop-blur-md shadow-xl overflow-hidden p-4 border border-slate-700/30">
                <div className="flex flex-col w-full space-y-4">
                    {/* 顶部标题 - 塔罗师选择 */}
                    <div className="py-2 px-3 mb-2">
                        <h2 className="text-lg font-medium text-white/80">
                            <SmartTitle>{t("choose_reader") || "选择塔罗师"}</SmartTitle>
                        </h2>
                    </div>

                    {/* 塔罗师选择选项 */}
                    <div className="mt-2 space-y-3">
                        {/* 暴风姐塔罗师 */}
                        <button
                            onClick={() => setReaderType('direct')}
                            className={`
                                group relative flex items-center space-x-3 px-4 py-3 rounded-lg w-full text-left
                                ${readerType === 'direct'
                                    ? `bg-gradient-to-r ${readerConfigs.direct.bgGradient} text-white`
                                    : 'text-white/80 hover:bg-white/5'}
                                transition-all duration-200
                            `}
                        >
                            <div className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden border-2 border-red-400/30">
                                <img 
                                    src={readerConfigs.direct.image}
                                    alt="暴风姐塔罗师"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="flex-1">
                                <div className="font-medium">
                                    <SmartText>{t("direct_reader") || "暴风姐塔罗师"}</SmartText>
                                </div>
                                <div className="text-xs text-white/60 mt-1">
                                    <SmartText>{t("direct_reader_desc") || "东北直球毒舌，实用主义解读"}</SmartText>
                                </div>
                            </div>
                            {readerType === 'direct' && (
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-400 rounded-full"></div>
                            )}
                        </button>

                        {/* 传统塔罗师 */}
                        <button
                            onClick={() => setReaderType('kind')}
                            className={`
                                group relative flex items-center space-x-3 px-4 py-3 rounded-lg w-full text-left
                                ${readerType === 'kind'
                                    ? `bg-gradient-to-r ${readerConfigs.kind.bgGradient} text-white`
                                    : 'text-white/80 hover:bg-white/5'}
                                transition-all duration-200
                            `}
                        >
                            <div className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden border-2 border-purple-400/30">
                                <img 
                                    src={readerConfigs.kind.image}
                                    alt="传统塔罗师"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="flex-1">
                                <div className="font-medium">
                                    <SmartText>{t("kind_reader") || "传统塔罗师"}</SmartText>
                                </div>
                                <div className="text-xs text-white/60 mt-1">
                                    <SmartText>{t("kind_reader_desc") || "倾向积极解读，着重正面引导"}</SmartText>
                                </div>
                            </div>
                            {readerType === 'kind' && (
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-400 rounded-full"></div>
                            )}
                        </button>
                    </div>

                    {/* 当前选择的塔罗师 */}
                    <div className="mt-6 pt-4">
                        <div className="h-px bg-gradient-to-r from-transparent via-slate-700/30 to-transparent mb-3"></div>
                        <div className={`px-4 py-3 rounded-lg ${config.cardBg}`}>
                            <div className="flex items-center">
                                <div className={`w-16 h-16 rounded-full overflow-hidden border-2 ${config.borderColor} border-opacity-30`}>
                                    <img 
                                        src={config.image}
                                        alt={readerType === 'direct' ? "暴风姐" : "传统塔罗师"}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="ml-3">
                                    <div className="text-sm font-medium text-white/90">
                                        <SmartText>
                                            {readerType === 'direct'
                                                ? (t("active_direct_reader") || "暴风姐")
                                                : (t("active_kind_reader") || "梅兰娜塔罗师")}
                                        </SmartText>
                                    </div>
                                    <div className="text-xs text-white/60 mt-0.5">
                                        <SmartText>
                                            {readerType === 'direct'
                                                ? (t("direct_reader_quote") || "老妹儿，姐用东北话给你整明白！")
                                                : (t("kind_reader_quote") || "让光明指引你的方向")}
                                        </SmartText>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 添加逆位模式开关 */}
                    <div className="mt-6 flex flex-col items-center space-y-2">
                        {/* 分隔线 */}
                        <div className="w-full max-w-xs h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent mb-2"></div>
                        {/* 切换按钮 */}
                        <button
                            type="button"
                            onClick={() => setUseReversals(!useReversals)}
                            className={cn(
                                "px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ease-in-out flex items-center space-x-2 shadow-md hover:shadow-lg",
                                useReversals
                                    ? `bg-gradient-to-r ${config.buttonGradient} text-white ring-2 ring-offset-2 ring-offset-slate-900 ${config.borderColor}` // 激活状态样式
                                    : 'bg-slate-800/60 text-white/70 hover:bg-slate-700/60 ring-1 ring-slate-700' // 非激活状态样式
                            )}
                        >
                            {/* 可以考虑加一个图标，比如 Lucide 的 RefreshCcw 或 Zap */}
                            {/* <RefreshCcw className={`w-4 h-4 transition-transform duration-300 ${useReversals ? 'rotate-180' : ''}`} /> */}
                            <span>
                                <SmartText>
                                    {useReversals ? tc("withReverse") : tc("withoutReverse")}
                                </SmartText>
                            </span>
                        </button>
                        {/* 说明文字 */}
                        <p className="text-xs text-white/60 text-center max-w-xs px-4 pt-1">
                            <SmartText>{t("reverse_mode_desc")}</SmartText>
                        </p>
                        {/* 分隔线 */}
                        <div className="w-full max-w-xs h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent mt-2"></div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col md:flex-row w-full gap-6">
            {/* 塔罗师选择侧边栏 */}
            {renderReaderSidebar()}

            {/* 主内容区域 */}
            <div className="relative w-full md:w-3/4 max-w-4xl mx-auto">
                {/* 神秘星空背景 */}
                <div className="absolute inset-0 overflow-hidden rounded-2xl">
                    <div className={`absolute inset-0 bg-gradient-to-b ${
                        readerType === 'direct' 
                            ? 'from-red-950 via-orange-950 to-slate-900'
                            : 'from-indigo-950 via-purple-950 to-slate-900'
                    } opacity-70`}></div>
                    {renderStars()}
                    <div className={`absolute inset-0 bg-gradient-radial ${
                        readerType === 'direct'
                            ? 'from-red-500/5 via-orange-700/5'
                            : 'from-indigo-500/5 via-purple-700/5'
                    } to-transparent bg-no-repeat bg-center bg-cover opacity-30 mix-blend-overlay`}></div>
                </div>

                {/* 主内容 */}
                <Card className="relative z-10 border-0 bg-transparent shadow-none overflow-hidden">
                    <div className="p-8 md:p-12">
                        {/* 标题区域 */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center mb-10"
                        >
                            <h2 className="text-4xl md:text-5xl font-bold mb-4">
                                <span className={`inline-block text-transparent bg-clip-text bg-gradient-to-r ${
                                    readerType === 'direct'
                                        ? 'from-red-300 to-orange-200'
                                        : 'from-purple-300 to-indigo-200'
                                } leading-normal pb-2`}>
                                    <SmartTitle>{t("title")}</SmartTitle>
                                </span>
                            </h2>
                            <p className={`text-lg ${
                                readerType === 'direct' ? 'text-orange-100/80' : 'text-purple-100/80'
                            } max-w-2xl mx-auto`}>
                                <SmartText>{t("description")}</SmartText>
                            </p>
                        </motion.div>

                        <CardContent className="p-0 space-y-8">
                            {/* 问题输入区域 */}
                            <div className="relative">
                                <form onSubmit={handleSubmit} className="relative">
                                    <div className={`relative rounded-xl overflow-hidden backdrop-blur-sm border ${config.borderColor}/30 bg-black/20 focus-within:border-${config.accentColor}/50 transition-all duration-300 shadow-lg`}>
                                        <textarea
                                            value={question}
                                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                                if (e.target.value.length <= MAX_CHARS) {
                                                    setQuestion(e.target.value);
                                                    setCharacterCount(e.target.value.length);
                                                }
                                            }}
                                            placeholder={t("input_placeholder")}
                                            className={cn(
                                                "py-7 px-6 bg-gradient-to-br",
                                                config.inputBg,
                                                "text-lg placeholder:text-white/40 border-0 w-full min-h-[100px] resize-y",
                                                "focus:outline-none focus:ring-0 text-white"
                                            )}
                                            disabled={isLoading}
                                            maxLength={MAX_CHARS}
                                        />
                                        <Button
                                            type="submit"
                                            disabled={isLoading}
                                            className={`absolute right-2 bottom-2 bg-gradient-to-r ${config.buttonGradient} ${config.buttonHover} rounded-lg p-3 shadow-lg`}
                                        >
                                            {isLoading ? (
                                                <Loader2 className="h-5 w-5 animate-spin text-white" />
                                            ) : (
                                                <Send className="h-5 w-5 text-white" />
                                            )}
                                        </Button>

                                        {/* 字符计数器 */}
                                        <div className={`absolute left-2 bottom-2 text-xs ${config.accentColor}/70`}>
                                            {characterCount}/{MAX_CHARS}
                                        </div>
                                    </div>

                                    {/* 提示按钮 */}
                                    <div className="absolute right-16 bottom-2">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setShowTip(!showTip)}
                                            className={`${config.accentColor} hover:text-white hover:bg-${config.cardBg}`}
                                        >
                                            <Info className="h-5 w-5" />
                                        </Button>
                                    </div>

                                    {/* 提示内容 */}
                                    <AnimatePresence>
                                        {showTip && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 10 }}
                                                className={`absolute right-0 top-full mt-2 p-4 ${config.cardBg} backdrop-blur-sm rounded-lg shadow-lg z-20 border ${config.borderColor}/30 w-full md:w-96`}
                                            >
                                                <p className="text-sm text-white/90">
                                                    <SmartText>
                                                        {t("tip_content")}
                                                    </SmartText>
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </form>
                            </div>

                            {/* 响应区域 */}
                            {(response || isLoading) && (
                                <div className="space-y-6">
                                    {isLoading ? (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="flex flex-col items-center justify-center py-12"
                                        >
                                            <div className="relative">
                                                <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${config.buttonGradient} blur-lg opacity-30 animate-pulse`}></div>
                                                <div className={`relative ${config.cardBg} p-4 rounded-full backdrop-blur-sm border ${config.borderColor}/20`}>
                                                    <Loader2 className="h-10 w-10 animate-spin text-white" />
                                                </div>
                                            </div>
                                            <p className="mt-4 text-white/80">
                                                <SmartText>{tc("think")}</SmartText>
                                            </p>
                                        </motion.div>
                                    ) : (
                                        <div className="space-y-6">
                                            {renderPreFlipMessage()}
                                            {renderCardContent()}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 错误信息显示 */}
                            {errorMessage && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`mb-6 p-4 ${config.cardBg} border ${config.borderColor}/30 rounded-lg text-center`}
                                >
                                    <p className={`${config.accentColor}`}>{errorMessage}</p>
                                </motion.div>
                            )}

                            {/* 底部提示区域 */}
                            <div className="pt-6 text-center">
                                <p className="text-sm text-purple-300/60 italic">
                                    <SmartText>{t("advice")}</SmartText>
                                </p>
                            </div>
                        </CardContent>
                    </div>
                </Card>

                {/* 底部装饰 */}
                <div className={`absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-${
                    readerType === 'direct' ? 'red-500/50' : 'purple-500/50'
                } to-transparent`}></div>

                {/* 使用正确的方式添加全局CSS */}
                <style dangerouslySetInnerHTML={{
                    __html: `
                        @keyframes twinkle {
                            0% { opacity: 0.3; }
                            100% { opacity: 1; }
                        }
                    `
                }} />
            </div>
        </div>
    );
}